import type { FC, ReactNode } from "react";
import { useState } from "react";

import type {
  Filter,
  CatalogSearchType,
  SearchFilterCategories,
  ProductSortField,
  ProductSortDirection,
  IngredientFilter,
  SupplementTypeFilter,
  CategoryFilter,
  ConditionFilter,
  Brand,
} from "@shared/components/CatalogSearch/CatalogSearch";
import { CatalogSearchContext } from "@shared/components/CatalogSearch/CatalogSearchContext";
import { useCatalogParams } from "@shared/hooks/useCatalogParams";

interface Props {
  children: ReactNode;
}

const CatalogSearchProvider: FC<Props> = ({ children }) => {
  const [catalogParams, setCatalogParams] = useCatalogParams({ preserveScroll: true });

  const certFilters: Filter[] = catalogParams.certifications || [];

  const formFilter: Filter[] = catalogParams.forms || [];

  const allergenFilter: Filter[] = catalogParams.allergens || [];

  const populationFilter: Filter[] = catalogParams.populations || [];

  const moreFilters: Filter[] = catalogParams.more || [];

  const activeFilters: Filter[] = [
    ...certFilters,
    ...formFilter,
    ...allergenFilter,
    ...populationFilter,
    ...moreFilters,
  ];

  const getActiveFilterByCategory = (filters: Filter[], category: SearchFilterCategories) =>
    filters?.filter(filter => filter.category === category);

  const setActiveFilters = (filters: Filter[]) => {
    const newFilters = {
      allergens: getActiveFilterByCategory(filters, "Allergens"),
      certifications: getActiveFilterByCategory(filters, "Certifications"),
      forms: getActiveFilterByCategory(filters, "Form"),
      populations: getActiveFilterByCategory(filters, "Demographic"),
      more: getActiveFilterByCategory(filters, "More"),
    };
    setCatalogParams({ ...catalogParams, ...newFilters });
  };

  const activeBrands: Brand[] = catalogParams.brands || [];

  const setActiveBrands = (brands: Brand[]) => {
    setCatalogParams({
      ...catalogParams,
      brands: brands.map(brand => ({ id: brand.id, name: brand.name, logo: "" })),
    });
  };

  const activeIngredients: IngredientFilter[] = catalogParams.ingredients || [];

  const setActiveIngredients = (ingredientFilters: IngredientFilter[]) => {
    setCatalogParams({
      ...catalogParams,
      ingredients: ingredientFilters,
    });
  };

  const excludedIngredients: IngredientFilter[] = catalogParams.excludedIngredients || [];

  const setExcludedIngredients = (ingredientFilters: IngredientFilter[]) => {
    setCatalogParams({
      ...catalogParams,
      excludedIngredients: ingredientFilters,
    });
  };

  const activeSupplementTypes: SupplementTypeFilter[] = catalogParams.supplementTypes || [];

  const setActiveSupplementTypes = (supplementTypeFilters: SupplementTypeFilter[]) => {
    setCatalogParams({
      ...catalogParams,
      supplementTypes: supplementTypeFilters,
    });
  };

  const activeCategories: CategoryFilter[] = catalogParams.categories || [];

  const setActiveCategories = (categoryFilters: CategoryFilter[]) => {
    setCatalogParams({
      ...catalogParams,
      categories: categoryFilters,
    });
  };

  const activeConditions: ConditionFilter[] = catalogParams.conditions || [];

  const setActiveConditions = (conditionFilters: ConditionFilter[]) => {
    setCatalogParams({
      ...catalogParams,
      conditions: conditionFilters,
    });
  };

  const searchQuery: string = catalogParams.query ?? "";
  const typeaheadId: string = catalogParams.typeaheadSelectionId ?? "";

  const setSearchQuery = (queryText: string, queryTypeaheadId?: string) => {
    if (!queryText) {
      const {
        query: unusedQuery,
        typeaheadSelectionId: unusedTypeaheadSelectionId,
        ...catalogParamsWithoutQuery
      } = catalogParams;
      setCatalogParams(catalogParamsWithoutQuery);
    } else if (!queryTypeaheadId) {
      const {
        typeaheadSelectionId: unusedTypeaheadSelectionId,
        ...catalogParamsWithoutTypeaheadSelectionId
      } = catalogParams;
      setCatalogParams({
        ...catalogParamsWithoutTypeaheadSelectionId,
        query: queryText,
      });
    } else {
      setCatalogParams({
        ...catalogParams,
        query: queryText,
        typeaheadSelectionId: queryTypeaheadId,
      });
    }
  };

  const sortField: ProductSortField = catalogParams.sortField;
  const sortDirection: ProductSortDirection = catalogParams.sortDirection;

  const setSort = (field: ProductSortField, direction: ProductSortDirection) => {
    setCatalogParams({ ...catalogParams, sortField: field, sortDirection: direction });
  };

  const [activeSearchType, setActiveSearchType] = useState<CatalogSearchType>("");

  const isOnlyShowFavorites = catalogParams.isOnlyShowFavorites;
  const setIsOnlyShowFavorites = (value: boolean) =>
    setCatalogParams({ ...catalogParams, isOnlyShowFavorites: value });

  const isOnlyShowInStock = catalogParams.isOnlyShowInStock;
  const setIsOnlyShowInStock = (value: boolean) =>
    setCatalogParams({ ...catalogParams, isOnlyShowInStock: value });

  const removeActiveFilter = (filterToRemove: Filter) => {
    setActiveFilters(
      activeFilters.filter(
        activeFilter =>
          activeFilter.name !== filterToRemove.name ||
          activeFilter.category !== filterToRemove.category
      )
    );
  };

  const isGridView = catalogParams.gridView === undefined ? true : catalogParams.gridView;
  const setGridView = (view: boolean) => {
    setCatalogParams({ ...catalogParams, gridView: view });
  };

  const onlyIngredient = catalogParams.onlyIngredient || false;
  const setOnlyIngredient = (toggle: boolean) => {
    setCatalogParams({ ...catalogParams, onlyIngredient: toggle });
  };

  return (
    <CatalogSearchContext.Provider
      value={{
        activeFilters,
        setActiveFilters,
        activeBrands,
        setActiveBrands,
        activeIngredients,
        setActiveIngredients,
        excludedIngredients,
        setExcludedIngredients,
        activeSupplementTypes,
        setActiveSupplementTypes,
        activeConditions,
        setActiveConditions,
        activeCategories,
        setActiveCategories,
        searchQuery,
        setSearchQuery,
        removeActiveFilter,
        activeSearchType,
        setActiveSearchType,
        sortDirection,
        sortField,
        setSort,
        isOnlyShowFavorites,
        setIsOnlyShowFavorites,
        isOnlyShowInStock,
        setIsOnlyShowInStock,
        isGridView,
        setGridView,
        onlyIngredient,
        setOnlyIngredient,
        typeaheadSelectionId: typeaheadId,
      }}
    >
      {children}
    </CatalogSearchContext.Provider>
  );
};

export { CatalogSearchProvider };
